import { useEffect, useRef } from 'react';
import { useAppDispatch } from 'app/hooks';
import { ahuBoardFormsApi } from 'app/services/ahuBoardFormsApi';
import {
  useGetComponentCmsDocQuery,
  useGetMasterListByUrlQuery,
} from 'app/services/ahuComponentFormsApi';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { COMPONENT_FORM_CONFIGS } from '../componentFormConfigs';
import { useComponentForm } from '../useComponentForm';
import { BareSelect, cmsList, FormSubtab, handleTabToNext, looseEq, type OptionRow } from '../formKit';
import { formatDecimalDisplay, formatIntegerDisplay } from '../formUtils';
import {
  BlankOffSlot,
  FormHeading,
  HeadingChrome,
  MBareInput,
  MBareSelect,
  MSelect,
  PBareSelect,
  RequiredFieldNote,
  usePricingMethods,
} from './formCommon';
import type { ComponentFormProps } from './ComponentFormHost';

/**
 * Phase 6e2a — FAN form, re-cut byte-faithful to components/fan/tmpl/
 * form.tmpl (verify round 1): the LIVE legacy fan tmpl is the TRIMMED
 * rating-mode variant unconditionally — Manufacturer/Model read-only lines,
 * disabled param selects, hidden heading (`rating-mode hide`, button
 * `vis-fan rating hide`), subtab census properties/performance/motor/
 * motor_base/isolation/mounting/acoustics/pricing (NO remarks; sheave_belts
 * is a li.hide stub), paired rows ISP|ESP|TSP / Power / Qty Wide x High.
 *
 * ESP suite + motor-voltage filter wiring unchanged (parity 316/317).
 */
const config = COMPONENT_FORM_CONFIGS.fan;

const FanForm = ({ row, section, section2, cms }: ComponentFormProps) => {
  const api = useComponentForm({
    config,
    row,
    section,
    section2,
    firstSubtab: 'properties',
  });
  const { model } = api;
  const dispatch = useAppDispatch();

  const { data: fanMountings } = useGetMasterListByUrlQuery(
    '/api/v1.0/master/v1.0.0/ahu/fan_mountings/'
  );
  // Spring isolator CMS doc — legacy fn['spring.isolator.cms']
  // (components/fan/model/spring.isolator.cms.js): a SEPARATE params doc;
  // the isolation subtab option lists live under its `spring_isolator.*`
  // nodes, NOT the fans params doc (verify 6e2a re-check).
  const { data: rawSpringIsolatorCms } = useGetComponentCmsDocQuery(
    '/api/v1.0/cms/1/v1.0.0/ahu/fans/isolators/spring/params/'
  );
  const springIsolatorCms = (Array.isArray(rawSpringIsolatorCms)
    ? (rawSpringIsolatorCms as unknown[])[0]
    : rawSpringIsolatorCms) as Record<string, unknown> | undefined;
  // Motor-base CMS doc — legacy fn['motor.base.cms'] (motor.base.cms.js:7),
  // loaded by the fan form as `additionalLoadableCMSSettings.cmsMotorBase`
  // (fan/view/form.js:344) and read by form.tmpl:927-956 as
  // `cmsMotorBase.motor_base.{manufacturer,series,mtl}`.
  const { data: rawMotorBaseCms } = useGetComponentCmsDocQuery(
    '/api/v1.0/cms/1/v1.0.0/ahu/motors/bases/params/'
  );
  const motorBaseCms = (Array.isArray(rawMotorBaseCms)
    ? (rawMotorBaseCms as unknown[])[0]
    : rawMotorBaseCms) as Record<string, unknown> | undefined;
  const pricingMethods = usePricingMethods();

  // ---- ESP suite (parity 317) ----
  const espChanged = useRef(false);
  const espDebounce = useRef<number | null>(null);
  const lastIsp = useRef<unknown>(undefined);
  const lastEsp = useRef<unknown>(undefined);
  useEffect(() => {
    if (!model) return undefined;
    const offs: Array<() => void> = [];

    const fetchEsp = () => {
      if (espDebounce.current) window.clearTimeout(espDebounce.current);
      espDebounce.current = window.setTimeout(() => {
        void dispatch(
          ahuBoardFormsApi.endpoints.getFanEsp.initiate(row.id, {
            subscribe: false,
            forceRefetch: true,
          })
        )
          .unwrap()
          .then((res) => {
            const esp = (res as { esp?: unknown }).esp;
            // eslint-disable-next-line eqeqeq
            if (esp != null && esp != model.get('esp')) {
              model.set({ esp }, { xhr: true });
              espChanged.current = true;
            }
          })
          .catch(() => undefined);
      }, 1000);
    };

    offs.push(ahuBoardBus.on('fetch:fans:esp', () => fetchEsp()));
    offs.push(
      ahuBoardBus.on(
        `force:render:fan:${row.id}` as `force:render:fan:${number}`,
        (...busArgs: unknown[]) => {
          const esp = busArgs[0];
          if (esp != null) model.set({ esp }, { xhr: true });
        }
      )
    );
    offs.push(
      ahuBoardBus.on(
        `refetch_fan_${section.id}_isp` as `refetch_fan_${number}_isp`,
        () => fetchEsp()
      )
    );

    // rerate:fan:data — master selected + esp_changed + BOTH isp & esp moved.
    offs.push(
      model.onChange((changed) => {
        if ('master_fan_id' in changed) {
          // AFTER the fan's own save, not on the `set`. What the dampers in
          // this section do with this is re-read themselves from the server:
          // `AHUAccessoryDamper.mounting_planes` offers Fan Intake only once
          // `section.components.first().master_fan` is set. (`allow_fan_intake`
          // does NOT depend on the fan — it is a stored field only the damper's
          // own `save()` writes, from the section type and from which damper
          // already holds the intake.)
          //
          // The wait matters for fan changes NOT made through the selector's
          // Select: that path has already saved `master_fan_id` server-side
          // (`guardSelect` awaits `POST fans/<id>/select`) before this `set`
          // runs, while the catalogue engine's sole-option auto-pick and its
          // deselect write client-side only, where `set` merely SCHEDULES the
          // PATCH and a damper refetching now would get the pre-fan answer.
          // `syncProcess` is armed synchronously by this same `set`, one step
          // after the listeners run, hence the microtask; it is an already
          // resolved promise when the model is not armed to save, so an
          // unsaved change still notifies immediately, as it used to. It is
          // the promise of whichever batch is in flight, so a `master_fan_id`
          // set while an earlier save is still going can still notify early —
          // an after-save hook through `useComponentForm` is the proper fix
          // (bot review #2056, follow-up 2).
          const sectionId = section.id;
          queueMicrotask(() => {
            void model.syncProcess.then(() =>
              ahuBoardBus.emit(
                `damper_change_params_${sectionId}` as `damper_change_params_${number}`
              )
            );
          });
        }
        const ispMoved =
          // eslint-disable-next-line eqeqeq
          'isp' in changed && lastIsp.current != changed.isp;
        const espMoved =
          // eslint-disable-next-line eqeqeq
          'esp' in changed && lastEsp.current != changed.esp;
        if ('isp' in changed) lastIsp.current = changed.isp;
        if ('esp' in changed) lastEsp.current = changed.esp;
        if (
          model.get('master_fan_id') &&
          espChanged.current &&
          ispMoved &&
          espMoved
        ) {
          espChanged.current = false;
          ahuBoardBus.emit('rerate:fan:data', {
            fan_id: row.id,
            section_id: section.id,
            isp: model.get('isp'),
            esp: model.get('esp'),
          });
        }
      })
    );

    return () => {
      offs.forEach((off) => off());
      if (espDebounce.current) window.clearTimeout(espDebounce.current);
    };
  }, [model, dispatch, row.id, section.id]);

  // ★ Shim #9 — fan_rating_curves_model lifecycle (§33.1). The selector STAMPS
  // the curves model on Apply (selectFan); a MANUAL master-fan pick from the
  // catalogue (legacy onManualModeSelection) DESTROYS it. The catalogue field
  // emits `select:model:for_<id>` on a manual selectModel; null the shim member
  // then (so a later rated-mode open doesn't render a stale curve over a
  // manually-picked fan).
  useEffect(() => {
    if (!model) return undefined;
    const off = ahuBoardBus.on(
      `select:model:for_${row.id}` as `select:model:for_${number}`,
      () => {
        const shim = window.__FORM_SHIM__?.[row.id];
        if (shim) shim.fan_rating_curves_model = null;
      }
    );
    return off;
  }, [model, row.id]);

  // motor voltage → motor filter index 2 (parity 316).
  useEffect(() => {
    if (!model) return undefined;
    const motor = api.catalogueFields.master_motor_id;
    if (!motor) return undefined;
    const apply = () => {
      const v = model.get('motor_voltage_id');
      if (v != null) motor.resetFilter({ voltage_id: v }, false, 2);
    };
    apply();
    return model.onChange((changed) => {
      if ('motor_voltage_id' in changed) apply();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, api.catalogueFields.master_motor_id]);

  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const sub = (name: string) => !!api.subtabs[name];
  const fanEngine = api.catalogueFields.master_fan_id;
  const cmsDoc = api.componentCms;

  const cmsRow = (path: string, id: unknown) =>
    cmsList(cmsDoc, path).find((r) => looseEq(r.id, id));
  const fanType = cmsRow('type', fanEngine?.paramValue('type_id'));
  const fanTypeValue = String(fanType?.value ?? '');
  const arrRow = cmsRow('arrangement', fanEngine?.paramValue('arr_id'));
  const arr3 = String(arrRow?.name ?? '') === '3';
  const isDwdi = fanTypeValue === 'dwdi';
  const isPlug = fanTypeValue === 'plug';
  const isCube = fanTypeValue === 'cube';
  const motorParams = (() => {
    try {
      return JSON.parse(String(model.get('motor_params') ?? '{}')) as Record<string, unknown>;
    } catch {
      return {} as Record<string, unknown>;
    }
  })();
  const motorBaseParams = (() => {
    try {
      return JSON.parse(String(model.get('motor_base_params') ?? '{}')) as Record<
        string,
        unknown
      >;
    } catch {
      return {} as Record<string, unknown>;
    }
  })();
  // eslint-disable-next-line eqeqeq
  const motorHp = motorParams.type_id == 1;
  const redundancyQty =
    Number(model.get('redundancy_id')) === 2
      ? Math.max((Number(model.get('fan_qty')) || 0) - 1, 0)
      : null;
  const dB = (inName: string, outName: string, label: string) => (
    <li key={inName}>
      <div className="frequency">
        <span className="unit">dB</span>
        <MBareInput api={api} name={inName} formatter="integer" readOnly extraClass="medium-input" />
        <MBareInput api={api} name={outName} formatter="integer" readOnly extraClass="medium-input" />
        <span className="label">{label}</span>
      </div>
    </li>
  );
  const roInput = (
    value: unknown,
    name?: string,
    extra = 'medium-input',
    formatter?: 'decimal' | 'integer'
  ) => (
    <input
      type="text"
      name={name}
      autoComplete="off"
      className={extra}
      data-formatter_type={formatter}
      value={
        formatter === 'decimal'
          ? formatDecimalDisplay(value)
          : formatter === 'integer'
            ? formatIntegerDisplay(value)
            : String(value ?? '')
      }
      readOnly
      tabIndex={-1}
      onChange={() => undefined}
    />
  );
  /**
   * Motor Base row (form.tmpl:925-960). Legacy renders a REAL select per row —
   * `.motor_base_params_field`, the `- -` placeholder, options off the
   * motor-base CMS doc — with the selected id read out of the model's
   * `motor_base_params` blob and mirrored into `span.selection`.
   *
   * It is inert by construction: the `master_motor_base_id` catalogue field is
   * commented out in the legacy fan view (fan/view/form.js:295-308), so nothing
   * binds `.motor_base_params_field` and a change never commits — which is why
   * the row reads as a disabled select on dev. The controlled select with a
   * no-op onChange reproduces that exactly; the previous cut used read-only
   * text inputs, which dropped the select chrome AND printed the raw id
   * instead of the CMS name.
   */
  const motorBaseRow = (
    name: 'mfg_id' | 'series_id' | 'mtl_id',
    cmsPath: string,
    label: string,
    labelKey?: string
  ) => (
    <li>
      <div className="single-line">
        <BareSelect
          name={name}
          fieldClass="motor_base_params_field"
          value={motorBaseParams[name]}
          options={cmsList(motorBaseCms, cmsPath)}
          labelKey={labelKey}
          onChange={() => undefined}
        />
        <span className="label">{label}</span>
      </div>
    </li>
  );
  const method = Number(model.get('pricing_method_id'));
  const motorMethod = Number(model.get('motor_pricing_method_id'));

  return (
    <>
      <FormHeading
        title="fan"
        api={api}
        wrapperClass="component-form-heading clearfix rating-mode hide"
        chrome={[
          {
            field: config.catalogueFields[0],
            visClass: 'vis-fan',
            buttonExtraClass: 'rating hide',
            showSmartlist: false,
            showReset: false,
            searchResultClass: 'search-result',
          },
        ]}
        withRatingBlock
        ratingBlockDataName="fan"
      />
      <form onKeyDown={handleTabToNext}>
        <ul className="inputs-group rating-mode" id="fan-form">
          <FormSubtab type="fan" cid={row.id} name="properties" label="" labelHidden open={sub('properties')} onToggle={api.toggleSubtab} sublistClass="max-height-auto">
            <li>
              <div className="single-line">
                {roInput(model.get('fan_mfg'), 'fan_mfg', '')}
                <span className="label">Manufacturer</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                {roInput(model.get('fan_model'), 'fan_model', '')}
                <span className="label">Model</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                {roInput(fanType?.name, 'type_id')}
                {isCube &&
                  String(fanEngine?.paramValue('mfg_id') ?? '') === '47' &&
                  roInput(
                    cmsRow('cube_housing', fanEngine?.paramValue('cube_housing_id'))?.name,
                    'cube_housing_id',
                    ''
                  )}
                <span className="label">
                  <span className="">Type</span>
                  {isCube && String(fanEngine?.paramValue('mfg_id') ?? '') === '47' && (
                    <>
                      {' '}
                      | <span> Housing </span>
                    </>
                  )}
                </span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <PBareSelect api={api} target="master_fan_id" name="series_id" cmsPath="series" labelKey="series" extraClass="medium-input" disabled />
                {isPlug && (
                  <PBareSelect api={api} target="master_fan_id" name="style_id" cmsPath="style" extraClass="rating medium-input" disabled />
                )}
                <span className="label">
                  <span> Series </span>
                  {isPlug && (
                    <>
                      {' '}
                      | <span> Style </span>
                    </>
                  )}
                </span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <PBareSelect api={api} target="master_fan_id" name="wheel_dia_nominal_id" cmsPath="wheel.nominal_diameter" labelKey="value" disabled />
                <span className="label">Wheel Dia</span>
              </div>
            </li>
            <li>
              <div className="">
                <span className="unit">%</span>{' '}
                {/* The two `{' '}` restore the inter-tag whitespace legacy's
                    form.tmpl:148-162 has and JSX drops. `.unit` is an in-flow
                    inline-block, so that space is rendered: without them the
                    width select sits 4px left of legacy and the class select
                    8px, breaking the right-edge alignment with Wheel Dia /
                    Wheel Material above and below. */}
                <PBareSelect api={api} target="master_fan_id" name="wheel_width_percent_id" cmsPath="wheel.width_percent" labelKey="value" extraClass="medium-input" disabled />{' '}
                {!isPlug && (
                  /* selectionStyle is form.tmpl:161's `style="margin-left: 20px"`
                     — the class mirror carries no margin rule of its own
                     (it is span:nth-of-type(3), so styles.css:3103's
                     `.selection:nth-of-type(2)` misses it). */
                  <PBareSelect api={api} target="master_fan_id" name="amca_class_id" cmsPath="amca_classes" extraClass="medium-input" selectionStyle={{ marginLeft: 20 }} disabled />
                )}
                <span className="label">
                  <span> Wheel Width </span>
                  {!isPlug && <span> | Class</span>}
                </span>
              </div>
            </li>
            <li>
              <div className="material-rotation single-line">
                <MBareSelect api={api} name="wheel_mtl_id" cmsPath="wheel.material" disabled />
                <span className="label">
                  <span>Wheel Material</span>
                </span>
              </div>
            </li>
            <li>
              <div className="material-rotation single-line">
                <PBareSelect api={api} target="master_fan_id" name="rotation_id" cmsPath="rotation" extraClass="medium-input" disabled />
                <span className="label">
                  <span>Rotation</span>
                </span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <span className="selection">
                  <span>{String(model.get('qty_wide') ?? '')}</span>
                </span>
                <select name="qty_wide" className="medium-input model_field" disabled />
                <span className="selection">
                  <span>{String(model.get('qty_high') ?? '')}</span>
                </span>
                <select name="qty_high" className="medium-input model_field" disabled />
                <span className="label">Qty Wide x High</span>
              </div>
            </li>
            {arr3 && (
              <>
                <li>
                  <div className="single-line">
                    <PBareSelect api={api} target="master_fan_id" name="shaft_mtl_id" cmsPath="shaft_material" />
                    <span className="label">Shaft Material</span>
                  </div>
                </li>
                <li>
                  <div>
                    <PBareSelect api={api} target="master_fan_id" name="bearing_life_id" cmsPath="bearing_life" />
                    <span className="label">Bearing Life</span>
                  </div>
                </li>
              </>
            )}
          </FormSubtab>

          <FormSubtab type="fan" cid={row.id} name="performance" label="Performance" open={sub('performance')} onToggle={api.toggleSubtab}>
            <li>
              <div className="">
                <span className="unit">CFM</span>
                <MBareSelect
                  api={api}
                  name="air_stream_id"
                  options={
                    ((cms as Record<string, unknown> | undefined)?.air as
                      | { air_stream?: OptionRow[] }
                      | undefined)?.air_stream ?? []
                  }
                  extraClass="medium-input"
                />
                <MBareInput api={api} name="airflow_actual" formatter="integer" extraClass="medium-input" />
                <span className="label">Air Stream &amp; Flow</span>
              </div>
            </li>
            <li>
              <div className="total-airflow with-small-inputs">
                <span className="unit">in WC</span>
                <BareRO api={api} name="isp" extraClass={`xxsmall-input fan_${row.id}`} />
                <BareRO api={api} name="esp" extraClass={`xxsmall-input ml-10 fan_${row.id}`} />
                <input type="text" autoComplete="off" className="opacity-0 ml-10 xxsmall-input" readOnly />
                <MBareInput api={api} name="tsp" extraClass="ml-10 xxsmall-input" />
                <span className="label">
                  <span>ISP</span> | <span>ESP</span> | <span>TSP</span>
                </span>
              </div>
            </li>
            <li>
              <div className="total-airflow with-small-inputs">
                <span className="unit">in WC</span>
                <BareRO api={api} name="esp_desired" extraClass={`medium-input ml-10 fan_${row.id}`} />
                <span className="label">
                  <span>ESP Desired</span>
                </span>
              </div>
            </li>
            <li>
              <div className="speed-controller">
                <span className="unit">RPM</span>
                <MBareInput api={api} name="speed" formatter="none" extraClass="medium-input" />
                <span className="label">
                  <span>Speed</span>
                </span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <MBareSelect api={api} name="redundancy_id" cmsPath="redundancy" extraClass="medium-input non-clickable" selectionClass=" non-clickable" disabled />
                <MBareSelect api={api} name="speed_condition_id" cmsPath="speed_condition" extraClass="medium-input non-clickable" selectionClass=" non-clickable" disabled />
                <span className="label">
                  <span>Redundancy</span> | <span>Speed Condition</span>
                </span>
              </div>
            </li>
            <li>
              <div className="multiply-total multiply">
                <span className="unit">{motorHp ? 'hp' : 'KW'}</span>
                <MBareInput api={api} name="bhp" valueName={motorHp ? 'bhp' : 'bp_kw'} extraClass="first-input small small-input" />
                <span className="multiply-sign">X</span>
                {roInput(redundancyQty ?? model.get('fan_qty'), 'qty', 'model_field small small-input second-input', 'integer')}
                <BareRO api={api} name="bhp_total_all_fans" valueName={motorHp ? 'bhp_total_all_fans' : 'bp_kw_total'} extraClass="total-output small small-input" />
                <span className="label">Power</span>
              </div>
            </li>
            {arr3 && (
              <li>
                <div className="multiply-total multiply">
                  <span className="unit">hp</span>
                  <BareRO api={api} name="bhp_with_belt_losses" extraClass="first-input small-input small" />
                  <span className="multiply-sign">X</span>
                  {roInput(model.get('fan_qty'), 'qty', 'model_field second-input small-input small', 'decimal')}
                  <BareRO api={api} name="bhp_with_belt_losses_total_all_fans" extraClass="total-output small-input small" />
                  <span className="label">Power w/Belt Loss</span>
                </div>
              </li>
            )}
            <li>
              <div className="two-units-label">
                <span className="unit">fpm</span>
                <BareRO api={api} name={isDwdi ? 'discharge_vel' : 'outlet_vel'} extraClass="medium-input" />
                <span className="unit long-unit">in WC</span>
                <BareRO api={api} name="apd" extraClass="performance-small" />
                <span className="label">
                  {isDwdi ? <span>Discharge Velocity </span> : <span> Outlet Velocity </span>} |{' '}
                  <span> VP</span>
                </span>
              </div>
            </li>
            <li>
              <div className="efficiency-mech">
                <span className="unit">%</span>
                <MBareInput api={api} name="static_efficiency" formatter="integer" readOnly extraClass="medium-input" />
                <MBareInput api={api} name="total_efficiency" formatter="integer" readOnly extraClass="medium-input" />
                <span className="label">
                  <span>Efficiency Static</span> | <span>Total</span>
                </span>
              </div>
            </li>
          </FormSubtab>

          <FormSubtab
            type="fan"
            cid={row.id}
            name="motor"
            label="Motor"
            open={sub('motor')}
            onToggle={api.toggleSubtab}
            headerChrome={
              <HeadingChrome
                api={api}
                spec={{
                  field: config.catalogueFields[1],
                  visClass: 'vis-motor',
                  showSmartlist: false,
                  showReset: false,
                  searchResultClass: 'search-result',
                }}
                wrapperClass="ahu-header-btns right hide"
              />
            }
          >
            <li>
              <div className="multiply">
                <MBareInput api={api} name="qty_wide" formatter="integer" readOnly extraClass="medium-input motor-section" />
                <span className="multiply-sign">X</span>
                <MBareInput api={api} name="qty_high" formatter="integer" readOnly extraClass="medium-input motor-section" />
                <span className="label">Qty Wide x High</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <PBareSelect api={api} target="master_motor_id" name="mfg_id" cmsPath="motor.manufacturer" />
                <span className="label">Manufacturer</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                {roInput(model.get('motor_model'), 'fan_model', '')}
                <span className="label">Model</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <PBareSelect api={api} target="master_motor_id" name="type_id" cmsPath="motor.type" extraClass="medium-input non-clickable" selectionClass=" non-clickable" disabled />
                <MBareInput api={api} name="qty_high" formatter="integer" readOnly extraClass="medium-input motor-section" />
                <span className="label">
                  <span>Type </span> |<span> Rotor</span>
                </span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <PBareSelect api={api} target="master_motor_id" name="series_id" cmsPath="motor.series" labelKey="series" />
                <span className="label">Series</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <PBareSelect api={api} target="master_motor_id" name="efficiency_id" cmsPath="motor.efficiency" />
                <span className="label">Efficiency</span>
              </div>
            </li>
            <li>
              <div className="enclosure-housing single-line">
                <PBareSelect api={api} target="master_motor_id" name="enclosure_id" cmsPath="motor.enclosure" extraClass="medium-input" />
                <PBareSelect api={api} target="master_motor_id" name="housing_id" cmsPath="motor.housing" extraClass="medium-input" />
                <span className="label">
                  <span>Enclosure</span> | <span>Housing</span>
                </span>
              </div>
            </li>
            <li>
              <div className="long_unit">
                <span className="unit">RPM</span>
                <PBareSelect api={api} target="master_motor_id" name="speed_nominal_id" cmsPath="motor.speed_nominal" labelKey="value" extraClass="long_unit" />
                <span className="label">Nominal Speed</span>
              </div>
            </li>
            <li>
              <div className="">
                <span className="unit">{motorHp ? 'hp' : 'KW'}</span>
                {roInput(model.get('motor_power'), undefined, '', 'decimal')}
                <span className="label">
                  <span>Power</span>
                </span>
              </div>
            </li>
            <li>
              <div className="multiply">
                <span className="unit" />
                <MBareSelect api={api} name="motor_voltage_id" cmsPath="motor.voltage" extraClass=" non-clickable" selectionClass="  non-clickable" disabled />
                <span className="label">
                  <span>Voltage</span>
                </span>
              </div>
            </li>
            {!!model.get('motor_nema_frame_id') && (
              <li>
                <div className="multiply">
                  {roInput(
                    cmsRow('motor.nema_frame', model.get('motor_nema_frame_id'))?.name,
                    'nema_frame_id',
                    ' motor_params_field_dis'
                  )}
                  <span className="label">
                    <span>NEMA Frame </span>
                  </span>
                </div>
              </li>
            )}
            {!!motorParams.ip_rating_id && (
              <li>
                <div className="multiply">
                  {roInput(
                    cmsRow('motor.ip_rating', motorParams.ip_rating_id)?.name,
                    'ip_rating_id',
                    ' motor_params_field_dis'
                  )}
                  <span className="label">
                    <span> IP Rating</span>
                  </span>
                </div>
              </li>
            )}
            <li>
              <div>
                <span className="unit">Amp</span>
                {roInput(model.get('motor_fla'), 'fla', 'motor_params_field_dis ', 'decimal')}
                <span className="label">Full Load</span>
              </div>
            </li>
            {/* eslint-disable-next-line eqeqeq */}
            {motorParams.type_id == '1' && (
              <li>
                <div className="single-line">
                  <PBareSelect api={api} target="master_motor_id" name="shaft_grounding_id" cmsPath="motor.shaft_grounding" />
                  <span className="label">Shaft Grounding</span>
                </div>
              </li>
            )}
            {arr3 && (fanTypeValue === 'plenum' || isDwdi) && (
              <li>
                <div className="single-line">
                  <MBareSelect
                    api={api}
                    name="motor_position_id"
                    cmsPath={
                      fanTypeValue === 'plenum'
                        ? 'motor_position.plenum_arr3'
                        : 'motor_position.dwdi_arr3'
                    }
                  />
                  <span className="label">Motor Position</span>
                </div>
              </li>
            )}
            <li>
              <div className="single-line">
                <MBareSelect api={api} name="mtr_controller_id" cmsPath="mtr_controller" extraClass=" non-clickable" selectionClass=" non-clickable" disabled />
                <span className="label">
                  <span>Controller</span>
                </span>
              </div>
            </li>
          </FormSubtab>

          <FormSubtab
            type="fan"
            cid={row.id}
            name="motor_base"
            label="Motor Base"
            open={sub('motor_base')}
            onToggle={api.toggleSubtab}
            liClass={model.get('master_fan_id') ? 'hide' : 'hide-'}
            headerChrome={null}
          >
            {motorBaseRow('mfg_id', 'motor_base.manufacturer', 'Manufacturer')}
            {motorBaseRow('series_id', 'motor_base.series', 'Series', 'series')}
            {motorBaseRow('mtl_id', 'motor_base.mtl', 'Material')}
          </FormSubtab>

          <FormSubtab
            type="fan"
            cid={row.id}
            name="isolation"
            label="Isolation"
            open={sub('isolation')}
            onToggle={api.toggleSubtab}
            liClass={isPlug ? 'hide' : undefined}
            headerChrome={
              <HeadingChrome
                api={api}
                spec={{
                  field: config.catalogueFields[2],
                  visClass: 'vis-isolation',
                  buttonExtraClass: 'mr-30',
                  showSmartlist: false,
                  searchResultClass: 'search-result',
                }}
                wrapperClass="ahu-header-btns sync-result hide"
              />
            }
          >
            <li>
              <div className="single-line">
                <MBareSelect api={api} name="si_mounting_method_id" options={cmsList(springIsolatorCms, 'spring_isolator.mounting_method')} />
                <span className="label">Mounting</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <PBareSelect api={api} target="master_spring_isolator_id" name="mfg_id" options={cmsList(springIsolatorCms, 'spring_isolator.manufacturer')} />
                <span className="label">Manufacturer</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <PBareSelect api={api} target="master_spring_isolator_id" name="series_id" options={cmsList(springIsolatorCms, 'spring_isolator.series')} labelKey="series" />
                <span className="label">Series</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <PBareSelect api={api} target="master_spring_isolator_id" name="isolator_type_id" options={cmsList(springIsolatorCms, 'spring_isolator.type')} />
                <span className="label">Type </span>
              </div>
            </li>
            <li>
              <div className="long_unit">
                {/* legacy form.tmpl:1032-1034 has a newline between the unit span
                    and the selection span, which HTML renders as a space; JSX drops
                    it, so the absolutely-positioned .selection lands ~3px left. */}
                <span className="unit">in</span>{' '}
                <MBareSelect api={api} name="si_specified_deflection_id" options={cmsList(springIsolatorCms, 'spring_isolator.specified_deflection')} extraClass="long_unit" />
                <span className="label">Desired Deflection</span>
              </div>
            </li>
          </FormSubtab>

          <FormSubtab type="fan" cid={row.id} name="mounting" label="Mounting" open={sub('mounting')} onToggle={api.toggleSubtab}>
            <li>
              <div className={`single-line ${String(model.get('mounting_id') ?? '')}`}>
                <span className="selection">
                  {(() => {
                    const hit = ((fanMountings ?? []) as OptionRow[]).find((r) =>
                      looseEq(r.id, model.get('mounting_id'))
                    );
                    return hit ? <span>{String(hit.name ?? '')}</span> : null;
                  })()}
                </span>
                <select
                  name="mfg_id"
                  className="fan_params_field editable"
                  disabled
                  value={String(model.get('mounting_id') ?? 'None')}
                  onChange={() => undefined}
                >
                  {!model.get('mounting_id') && <option value="None">- -</option>}
                  {(() => {
                    const hit = ((fanMountings ?? []) as OptionRow[]).find((r) =>
                      looseEq(r.id, model.get('mounting_id'))
                    );
                    return hit ? (
                      <option className={String(model.get('mounting_id'))} value={String(hit.id)}>
                        {String(hit.name ?? '')}
                      </option>
                    ) : null;
                  })()}
                </select>
                <span className="label ">Mounting</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <PBareSelect api={api} target="master_fan_id" name="arr_id" cmsPath="arrangement" disabled />
                <span className="label">Arrangement</span>
              </div>
            </li>
            {(() => {
              // legacy form.tmpl 1080-1144: per-type orientation row
              const arrName = String(arrRow?.value ?? arrRow?.name ?? '');
              const orientationPath = isDwdi
                ? 'orientation.dwdi_arr3'
                : fanTypeValue === 'plenum' && arrName === '3'
                  ? 'orientation.plenum_arr3'
                  : fanTypeValue === 'plenum' && arrName === '4'
                    ? 'orientation.plenum_arr4'
                    : isPlug
                      ? 'orientation.plug_arr4'
                      : null;
              if (!orientationPath) return null;
              return (
                <li>
                  <div className="single-line">
                    <MBareSelect api={api} name="orientation_id" cmsPath={orientationPath} selectionClass=" " />
                    <span className="label">Orientation </span>
                  </div>
                </li>
              );
            })()}
            <li>
              <div className="air-pd single-line">
                <span className="selection editable" />
                <select id="housed_discharge" name="housed_discharge_id" className="fan_params_field" required defaultValue="None">
                  <option value="None">- -</option>
                </select>
                <span className="label">Housed Discharge</span>
              </div>
            </li>
            <li className="show-if active">
              <div>
                <span className="unit">in</span>
                <MBareInput api={api} name="assy_height_from_floor" />
                <span className="label">Distance from Floor</span>
              </div>
            </li>
            <li className="show-if active">
              <div>
                <span className="unit">in</span>
                <MBareInput api={api} name="assy_distance_from_ceiling" />
                <span className="label">Distance from Ceiling</span>
              </div>
            </li>
            <li className="show-if active">
              <div>
                <span className="unit">in</span>
                <MBareInput api={api} name="assy_distance_from_section_ups" />
                <span className="label">Distance from Section Ups</span>
              </div>
            </li>
          </FormSubtab>

          <FormSubtab type="fan" cid={row.id} name="acoustics" label="Acoustics" open={sub('acoustics')} onToggle={api.toggleSubtab}>
            <li className="sublist-heading three-headings">
              <h3>Frequency</h3>
              <h3>Inlet</h3>
              <h3>Outlet</h3>
            </li>
            {dB('dbi_63', 'dbo_63', '@ 63 Hz')}
            {dB('dbi_125', 'dbo_125', '@ 125 Hz')}
            {dB('dbi_250', 'dbo_250', '@ 250 Hz')}
            {dB('dbi_500', 'dbo_500', '@ 500 Hz')}
            {dB('dbi_1000', 'dbo_1000', '@ 1000 Hz')}
            {dB('dbi_2000', 'dbo_2000', '@ 2000 Hz')}
            {dB('dbi_4000', 'dbo_4000', '@ 4000 Hz')}
            {dB('dbi_8000', 'dbo_8000', '@ 8000 Hz')}
            {dB('in_lwa', 'out_lwa', 'LwA')}
          </FormSubtab>

          <FormSubtab type="fan" cid={row.id} name="pricing" label="Pricing" open={sub('pricing')} onToggle={api.toggleSubtab} headerChrome={null}>
            <MSelect api={api} name="pricing_method_id" label="Fan : Pricing Method" options={pricingMethods} hideNone extraClass="single-line" />
            {method !== 1 && (
              <li>
                <div className="frequency">
                  <span className="unit">US$</span>
                  <MBareInput api={api} name={method === 2 ? 'cost_product_user' : 'net_price_product_user'} extraClass="text-left medium-input" />
                  <MBareInput api={api} name={method === 2 ? 'cost_freight_user' : 'net_cost_freight_user'} extraClass="text-left medium-input" />
                  <span className="label">Fan : Mtl | Freight</span>
                </div>
              </li>
            )}
            <MSelect api={api} name="motor_pricing_method_id" label="Motor : Pricing Method" options={pricingMethods} hideNone extraClass="single-line" />
            {motorMethod !== 1 && (
              <li>
                <div className="frequency">
                  <span className="unit">US$</span>
                  <MBareInput api={api} name={motorMethod === 2 ? 'motor_cost_product_user' : 'motor_net_price_product_user'} extraClass="text-left medium-input" />
                  <MBareInput api={api} name={motorMethod === 2 ? 'motor_cost_freight_user' : 'motor_net_price_freight_user'} extraClass="text-left medium-input" />
                  <span className="label">Motor : Mtl | Freight</span>
                </div>
              </li>
            )}
          </FormSubtab>

          <BlankOffSlot api={api} sectionId={section.id} />
        </ul>
      </form>
      <RequiredFieldNote />
    </>
  );
};

/** Read-only model-bound input WITHOUT the model_field class (legacy RO
 * performance fields carry only size classes + the fan_<id> hook). */
const BareRO = ({
  api,
  name,
  valueName,
  extraClass,
}: {
  api: ReturnType<typeof useComponentForm>;
  name: string;
  /** legacy keeps the bhp names while displaying the KW values (form.tmpl 553). */
  valueName?: string;
  extraClass: string;
}) => (
  <input
    type="text"
    name={name}
    autoComplete="off"
    data-formatter_type="decimal"
    className={`model_field ${extraClass}`}
    value={formatDecimalDisplay(api.model?.get(valueName ?? name))}
    readOnly
    tabIndex={-1}
    onChange={() => undefined}
  />
);

export default FanForm;
